<template>
  <c-form @submit.prevent="submit">

    <c-title>Registrierung</c-title>

    <c-grid>

      <c-grid-item :span="6">
        <c-form-input
          id="first-name"
          :errors="[{
            message: 'Dieses Feld ist erforderlich.',
            show: !$v.firstName.required
          }]"
          :has-error="$v.firstName.$error"
          :is-valid="!$v.firstName.$invalid"
          v-model="firstName"
          aria-required="true"
          autocomplete="given-name"
          label="Vorname"
          @input="$v.firstName.$touch"
        />
      </c-grid-item>

      <c-grid-item :span="6">
        <c-form-input
          id="last-name"
          :errors="[{
            message: 'Dieses Feld ist erforderlich.',
            show: !$v.lastName.required
          }]"
          :has-error="$v.lastName.$error"
          :is-valid="!$v.lastName.$invalid"
          v-model="lastName"
          aria-required="true"
          autocomplete="family-name"
          label="Nachname"
          @input="$v.lastName.$touch"
        />
      </c-grid-item>

      <c-form-input
        id="email"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.email.required
        }, {
          message: 'Keine gültige E-Mail-Adresse.',
          show: !$v.email.email
        }]"
        :has-error="$v.email.$error"
        :is-valid="!$v.email.$invalid"
        v-model="email"
        aria-required="true"
        autocomplete="email"
        label="E-Mail-Adresse"
        type="email"
        @input="$v.email.$touch"
      />

      <c-form-input
        id="password"
        ref="password"
        :errors="[]"
        :has-error="false"
        :is-valid="!$v.password.$invalid"
        v-model="password"
        aria-required="true"
        autocomplete="new-password"
        label="Passwort"
        type="password"
        @input="$v.password.$touch"
      />

      <c-password-strength-indicator 
        :password="password" 
        :show-requirements="true" 
      />

      <c-form-input
        id="password-repeat"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.passwordRepeat.required
        }, {
          message: 'Ihre Passwörter müssen übereinstimmen.',
          show: !$v.passwordRepeat.sameAs
        }]"
        :has-error="$v.passwordRepeat.$error && $v.password.$dirty"
        :is-valid="!$v.passwordRepeat.$invalid"
        v-model="passwordRepeat"
        aria-required="true"
        autocomplete="new-password"
        label="Passwort wiederholen"
        type="password"
        @input="$v.passwordRepeat.$touch"
      />

    </c-grid>

    <c-button :busy="saveStatus === $config.status.SAVING">
      Konto erstellen
    </c-button>

    <router-link
      v-if="token"
      :to="{
        name: 'login',
        params: {
          token: token
        }
      }"
    >
      Mit bestehendem Konto anmelden
    </router-link>

    <router-link
      v-if="!token"
      :to="{
        name: 'login',
      }"
    >
      Mit bestehendem Konto anmelden
    </router-link>

  </c-form>
</template>

<script>
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CFormInput from '@/components/form/CFormInput'
import CPasswordStrengthIndicator from '@/components/form/CPasswordStrengthIndicator'
import CGrid from '@/components/CGrid'
import CGridItem from '@/components/CGridItem'
import CTitle from '@/components/CTitle'

import Cookies from 'js-cookie'
import handleRequestError from '@/functions/handleRequestError'
import sessionToken from '@/functions/sessionToken'

import { email, minLength, required, sameAs } from 'vuelidate/lib/validators'
import { strongPassword } from '@/functions/validators'

export default {
  name: 'VSignup',

  components: {
    CButton,
    CForm,
    CFormInput,
    CPasswordStrengthIndicator,
    CGrid,
    CGridItem,
    CTitle
  },

  props: {
    token: {
      type: String
    }
  },

  data () {
    return {
      saveStatus: this.$config.status.IDLE,
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordRepeat: ''
    }
  },

  validations: {
    firstName: {
      required
    },
    lastName: {
      required
    },
    email: {
      minLength: minLength(5),
      email,
      required
    },
    password: {
      required,
      strongPassword
    },
    passwordRepeat: {
      sameAs: sameAs('password'),
      required
    }
  },
  created () {
    if (this.token && !sessionToken.get()) {
      this.$api.get('projects/registration-token', {
        params: { identifier: this.token }
      })
        .then(response => {
          this.firstName = response.data.first_name
          this.lastName = response.data.last_name
          this.email = response.data.email
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihre Registrierung kann zurzeit leider nicht durchgeführt werden. Bitte versuchen Sie es später erneut.'
          })
        })
    } else {
      if (sessionToken.get() && this.token) {
        this.$api.patch(`projects/registration-token/${this.token}/`, {
          identifier: this.token
        })
          .then(response => {
            this.$store.commit('notifications/add', {
              type: 'success',
              text: 'Sie wurden erfolgreich freigeschaltet.'
            })
            this.$router.push({ name: 'dashboard' })
          })
          .catch(error => {
            handleRequestError({
              error,
              fallback: 'Bei der Freischaltung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
            })
          })
      }
    }
  },

  methods: {
    submit () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$store.commit('notifications/add', {
          text: 'Bitte füllen Sie alle Felder korrekt aus.',
          type: 'error'
        })

        return
      }

      this.saveStatus = this.$config.status.SAVING

      this.$api.post('auth/signup/', {
        first_name: this.firstName,
        last_name: this.lastName,
        email: this.email,
        password: this.password,
        token: this.token || ''
      }, {
        headers: { 'X-CSRFToken': Cookies.get('csrftoken') }
      })
        .then(response => {
          this.$store.commit('notifications/add', {
            type: 'success',
            text: `Sie haben eine E-Mail zugeschickt bekommen.
            Klicken Sie auf den darin enthaltenen Link, um Ihr Profil freizuschalten.
            Sollten Sie länger als 5 Tage warten, wird Ihr Account wieder gelöscht.`
          })
          this.$router.push({ name: 'login' })
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihre Registrierung kann zurzeit leider nicht durchgeführt werden. Bitte versuchen Sie es später erneut.'
          })

          this.password = ''
          this.passwordRepeat = ''
          this.saveStatus = this.$config.status.ERROR

          this.$refs.password.focus()
          this.$v.$touch()
        })
    }
  }
}
</script>
